import { buildBoats, boatContains, type Boat } from './boats';
import { DROWN_DEPTH, buildTerrain, type LevelDef } from './levels';
import { AIR_STEER, GRAVITY, JUMP_VX, JUMP_VY, MAX_FALL, STEP_UP, UNIT_H, UNIT_HALF_W } from './physics';
import type { Terrain } from './terrain';

/**
 * Can a mole walk from one team to the other? A coarse graph of the places a mole can stand
 * (every 8 px along the ground, every surface in the column), joined by the three ways a mole
 * moves: a walking step (up to STEP_UP per tick, down any drop that is survivable), a jump (the
 * real launch, gravity and air steering, simulated tick by tick) and a plain fall off an edge.
 * Read by the map tests and by the generator, which lays a bridge wherever the graph is split.
 */

export type SolidFn = (x: number, y: number) => boolean;

export const GRID = 8;
/** Longest fall an edge may take, px: well under the drop that would hurt much. */
export const MAX_DROP = 260;

export interface Spot {
  x: number;
  y: number;
}

export interface WalkGraph {
  nodes: Spot[];
  edges: number[][];
  byCol: Map<number, number[]>;
  width: number;
  deathY: number;
}

/** Terrain mask plus every boat's hull, as the sim sees solidity at the start of a fight. */
export function levelSolid(level: LevelDef, terrain: Terrain = buildTerrain(level), boats: Boat[] = buildBoats(level.boats)): SolidFn {
  return (x, y) => terrain.isSolid(x, y) || boats.some((b) => boatContains(b, x, y));
}

export function deathLine(level: LevelDef): number {
  return level.waterY !== null ? level.waterY + DROWN_DEPTH : level.height;
}

/** How much of a mole's movement the graph models: walking and falling only, plus a plain jump, or plus braking hops. */
export type Jumps = 'none' | 'basic' | 'full';

export function buildWalkGraph(level: LevelDef, solid: SolidFn = levelSolid(level), jumps: Jumps = 'full'): WalkGraph {
  const foot = (x: number, y: number): boolean => solid(x, y) || solid(x - UNIT_HALF_W * 0.55, y) || solid(x + UNIT_HALF_W * 0.55, y);
  const body = (x: number, y: number): boolean => {
    for (let dy = 6; dy < UNIT_H; dy += 8) if (solid(x - UNIT_HALF_W * 0.6, y - dy) || solid(x + UNIT_HALF_W * 0.6, y - dy)) return true;
    return false;
  };
  const deathY = deathLine(level);
  const nodes: Spot[] = [];
  const byCol = new Map<number, number[]>();
  const colOf = (x: number): number => Math.round(x / GRID);
  for (let col = Math.ceil((UNIT_HALF_W + 2) / GRID); col * GRID < level.width - UNIT_HALF_W - 2; col++) {
    const x = col * GRID;
    const ids: number[] = [];
    for (let y = UNIT_H; y < deathY - 2; y++) {
      if (!foot(x, y) && foot(x, y + 1) && !body(x, y)) {
        ids.push(nodes.length);
        nodes.push({ x, y });
        // Skip down through the solid below this surface.
        while (y < deathY - 2 && foot(x, y + 1)) y++;
      }
    }
    if (ids.length) byCol.set(col, ids);
  }
  const edges: number[][] = nodes.map(() => []);
  const nodeAt = (x: number, y: number, tol: number): number => {
    const ids = byCol.get(colOf(x));
    if (!ids) return -1;
    let best = -1;
    let bestD = tol + 1;
    for (const id of ids) {
      const d = Math.abs(nodes[id]!.y - y);
      if (d < bestD) {
        bestD = d;
        best = id;
      }
    }
    return best;
  };
  const J = level.jump;
  const G = level.gravity;
  nodes.forEach((n, id) => {
    const out = edges[id]!;
    const add = (to: number): void => {
      if (to >= 0 && to !== id && !out.includes(to)) out.push(to);
    };
    for (const dir of [-1, 1] as const) {
      // Walk (or walk off an edge and fall).
      const nx = n.x + dir * GRID;
      let ny: number | null = null;
      for (let up = 0; up <= STEP_UP; up += 2) {
        const yy = n.y - up;
        if (foot(nx, yy)) continue;
        if (body(nx, yy)) break;
        ny = yy;
        break;
      }
      if (ny !== null) {
        let yy = ny;
        let fell = 0;
        while (!foot(nx, yy + 1) && yy < deathY && fell <= MAX_DROP) {
          yy++;
          fell++;
        }
        if (fell <= MAX_DROP && yy < deathY - 4) add(nodeAt(nx, yy, fell > 0 ? 3 : 6));
      }
      // Jump with the direction held, or held for a while and then reversed to brake (short hops).
      for (const brakeAt of jumps === 'none' ? [] : jumps === 'basic' ? [Infinity] : [Infinity, 6, 12, 20]) {
      let px = n.x;
      let py = n.y;
      let vx = dir * JUMP_VX * J;
      let vy = -JUMP_VY * J;
      for (let t = 0; t < 200; t++) {
        vy = Math.min(vy + GRAVITY * G, MAX_FALL);
        vx += ((t >= brakeAt ? -dir : dir) * JUMP_VX * J - vx) * AIR_STEER;
        const jx = px + vx;
        if (jx > UNIT_HALF_W && jx < level.width - UNIT_HALF_W && !body(jx, py)) px = jx;
        else vx *= 0.5;
        let remaining = vy;
        const sign = Math.sign(remaining);
        let landed = false;
        while (Math.abs(remaining) > 0) {
          const dy = sign * Math.min(3, Math.abs(remaining));
          remaining -= dy;
          const jy = py + dy;
          if (dy > 0 && foot(px, jy)) {
            let ly = jy;
            for (let i = 0; i < 14 && foot(px, ly); i++) ly -= 1;
            py = ly;
            landed = true;
            break;
          }
          if (dy < 0 && solid(px, jy - UNIT_H)) {
            vy = 0;
            break;
          }
          py = jy;
        }
        vx *= 0.995;
        if (landed) {
          add(nodeAt(px, py, 10));
          break;
        }
        if (py > deathY) break;
      }
      }
    }
  });
  return { nodes, edges, byCol, width: level.width, deathY };
}

/** The node whose spot is nearest (x, y) within a small box, or -1. */
export function nodeNear(g: WalkGraph, x: number, y: number, dx = 24, dy = 70): number {
  let best = -1;
  let bestD = Infinity;
  const c0 = Math.floor((x - dx) / GRID);
  const c1 = Math.ceil((x + dx) / GRID);
  for (let c = c0; c <= c1; c++) {
    for (const id of g.byCol.get(c) ?? []) {
      const n = g.nodes[id]!;
      if (Math.abs(n.y - y) > dy) continue;
      const d = Math.hypot(n.x - x, (n.y - y) * 0.5);
      if (d < bestD) {
        bestD = d;
        best = id;
      }
    }
  }
  return best;
}

/** Every node a mole can reach from `from` (1 = reachable). */
export function reachableFrom(g: WalkGraph, from: number): Uint8Array {
  const seen = new Uint8Array(g.nodes.length);
  if (from < 0) return seen;
  const stack = [from];
  seen[from] = 1;
  while (stack.length) {
    const id = stack.pop()!;
    for (const to of g.edges[id]!) {
      if (!seen[to]) {
        seen[to] = 1;
        stack.push(to);
      }
    }
  }
  return seen;
}

/** Every node that can reach `to` (walking the edges backwards). */
export function reachesTo(g: WalkGraph, to: number): Uint8Array {
  const rev: number[][] = g.nodes.map(() => []);
  g.edges.forEach((out, id) => out.forEach((t) => rev[t]!.push(id)));
  const seen = new Uint8Array(g.nodes.length);
  if (to < 0) return seen;
  const stack = [to];
  seen[to] = 1;
  while (stack.length) {
    const id = stack.pop()!;
    for (const from of rev[id]!) {
      if (!seen[from]) {
        seen[from] = 1;
        stack.push(from);
      }
    }
  }
  return seen;
}

export interface Connectivity {
  /** A mole at the left team's first spawn can walk to the right team's first spawn. */
  forward: boolean;
  /** And the other way round. */
  back: boolean;
  /** Spawn nodes that could not be found at all (a spawn floating in the air). */
  missing: string[];
}

/** Whether the two teams' first spawns are joined by a walking route, both ways. */
export function connectivity(level: LevelDef, graph: WalkGraph = buildWalkGraph(level)): Connectivity {
  const missing: string[] = [];
  const a = level.spawns[0]?.[0];
  const b = level.spawns[1]?.[0];
  const ia = a ? nodeNear(graph, a[0], a[1]) : -1;
  const ib = b ? nodeNear(graph, b[0], b[1]) : -1;
  if (ia < 0) missing.push('left spawn');
  if (ib < 0) missing.push('right spawn');
  if (ia < 0 || ib < 0) return { forward: false, back: false, missing };
  return { forward: reachableFrom(graph, ia)[ib] === 1, back: reachableFrom(graph, ib)[ia] === 1, missing };
}

/**
 * Whether both teams' first spawns are joined by a walking route, trying the cheap models first:
 * most maps are connected by walking, falling and a plain jump, and only the odd one needs the
 * full braking-hop graph to prove it.
 */
export function isConnected(level: LevelDef, solid: SolidFn = levelSolid(level)): boolean {
  for (const jumps of ['none', 'basic', 'full'] as const) {
    const c = connectivity(level, buildWalkGraph(level, solid, jumps));
    if (c.forward && c.back) return true;
    if (c.missing.length) return false;
  }
  return false;
}
